import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp, Language, OpenInNew } from "@mui/icons-material";
import { toast } from "react-toastify";
import { format } from "date-fns";
import api from "../../../api";
import { similarityColors } from "./SimilarityReportDialog";

/**
 * Web plagiarism chip for a list row. Only shown once a web check has been run
 * (the check is manual and paid, so most rows never have one).
 */
export const WebSimilarityChip = ({ job, onClick, height = 17, fontSize = "0.6rem" }) => {
  const status = job?.web_similarity_status;
  if (!status) return null;
  const percent = job?.web_similarity_percent != null ? Number(job.web_similarity_percent) : null;
  const base = { height, fontSize, fontWeight: 600, cursor: "pointer", ml: 0.5 };

  let label;
  let sx;
  if (status === "pending") {
    label = "Checking web…";
    sx = { bgcolor: "#f5f5f5", color: "#616161" };
  } else if (status === "done" && percent != null) {
    const c = similarityColors(percent);
    label = `Web ${percent.toFixed(0)}%`;
    sx = { bgcolor: c.bgcolor, color: c.color };
  } else if (status === "failed") {
    label = "Web check failed";
    sx = { bgcolor: "#ffebee", color: "#b71c1c" };
  } else {
    label = "Web: nothing to check";
    sx = { bgcolor: "transparent", color: "#9e9e9e", border: "1px dashed #bdbdbd" };
  }

  return (
    <Tooltip title="Share of this content found on other websites. Click for details.">
      <Chip icon={<Language sx={{ fontSize: "0.85em !important" }} />} label={label} size="small" onClick={onClick} sx={{ ...base, ...sx }} />
    </Tooltip>
  );
};

const WebSourceRow = ({ source, thresholds }) => {
  const [open, setOpen] = useState(false);
  const c = similarityColors(source.percent, thresholds);
  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          {source.spans?.length > 0 && (
            <IconButton size="small" onClick={() => setOpen((v) => !v)}>
              {open ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ maxWidth: 360 }}>
          <Link href={source.url} target="_blank" rel="noopener noreferrer" underline="hover"
            sx={{ fontWeight: 600, fontSize: "0.875rem", wordBreak: "break-word", display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            {source.title || source.url}
            <OpenInNew sx={{ fontSize: "0.9rem" }} />
          </Link>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", wordBreak: "break-all" }}>
            {source.domain}
            {source.matched_queries > 0 && ` · found by ${source.matched_queries} search${source.matched_queries === 1 ? "" : "es"}`}
          </Typography>
        </TableCell>
        <TableCell align="right">
          {source.fetched ? (
            <Chip size="small" label={`${Number(source.percent).toFixed(1)}%`} sx={{ fontWeight: 700, bgcolor: c.bgcolor, color: c.color }} />
          ) : (
            <Tooltip title={`Page could not be read (${source.fetch_error || "unknown error"}). The search still found an exact sentence there; open it to compare.`}>
              <Chip size="small" variant="outlined" label="Phrase found" sx={{ fontWeight: 600 }} />
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={3} sx={{ py: 0, borderBottom: open ? undefined : "none" }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ py: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Our text that also appears on this page ({source.shared_fingerprints} shared fingerprints). Text is shown normalised.
              </Typography>
              {source.spans.map((span, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 1, bgcolor: "#fffde7", borderColor: "#fff59d" }}>
                  <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{span.text}</Typography>
                  <Typography variant="caption" color="text.secondary">~{span.words} words</Typography>
                </Paper>
              ))}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

/**
 * "On the web" part of the similarity report: is this content published on other websites?
 * Data: GET {base}/web-similarity, run: POST {base}/web-similarity/check (WebSimilarityService).
 * The check is manual because every run costs paid web searches.
 *
 * @param base      API base of the entity, e.g. /admin/mock-exam/12
 * @param isImport  uploaded past papers are expected to be online (the school's own site)
 */
const WebSimilaritySection = ({ base, isImport = false, onChecked }) => {
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!base) return;
    setLoading(true);
    try {
      const res = await api.get(`${base}/web-similarity`);
      setData(res.data?.data || null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load web check");
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  // While a check is pending, poll until it finishes.
  useEffect(() => {
    if (data?.status !== "pending") return undefined;
    const t = setTimeout(load, 5000);
    return () => clearTimeout(t);
  }, [data, load]);

  const handleCheck = async () => {
    setChecking(true);
    try {
      await api.post(`${base}/web-similarity/check`);
      toast.success("Web check queued");
      setData((prev) => ({ ...(prev || {}), status: "pending" }));
      onChecked?.();
    } catch (err) {
      toast.error(err.response?.data?.message || "Web check failed to start");
    } finally {
      setChecking(false);
    }
  };

  const report = data?.report;
  const hasResult = report && report.percent != null;
  const thresholds = report?.thresholds || { warn: 20, high: 50 };
  const percent = data?.percent ?? report?.percent ?? null;
  const colors = similarityColors(percent, thresholds);
  const pending = data?.status === "pending";

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75, flexWrap: "wrap" }}>
        <Language fontSize="small" color="action" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>On the web</Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title={`Searches up to ${data?.max_queries ?? 6} distinctive sentences on Google (paid searches; repeat searches are cached for 30 days).`}>
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={checking || pending ? <CircularProgress size={14} /> : <Language />}
              disabled={checking || pending || loading || data?.configured === false}
              onClick={handleCheck}
              sx={{ textTransform: "none" }}
            >
              {hasResult ? "Check the web again" : "Check the web"}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {loading && !data ? (
        <LinearProgress sx={{ my: 1 }} />
      ) : data?.configured === false ? (
        <Alert severity="info">Web search is not set up yet. Add <code>SERPER_API_KEY</code> to the backend <code>.env</code>.</Alert>
      ) : pending ? (
        <Alert severity="info" icon={<CircularProgress size={18} />}>Searching the web. This updates automatically.</Alert>
      ) : (
        <>
          {data?.status === "failed" && (
            <Alert severity="error" sx={{ mb: 1 }}>
              The web check failed{report?.error ? `: ${report.error}` : "."}
              {hasResult ? " The previous result is shown below." : ""}
            </Alert>
          )}
          {data?.status === "skipped" && !hasResult && (
            <Alert severity="info">No sentences long enough to search (image-only or very short content).</Alert>
          )}
          {!data?.status && (
            <Typography variant="body2" color="text.secondary">
              Not checked yet. Click <strong>Check the web</strong> to search other websites for this content.
            </Typography>
          )}

          {hasResult && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 0.75, flexWrap: "wrap" }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: colors.color }}>{Number(percent).toFixed(1)}%</Typography>
                  <Typography variant="body2" color="text.secondary">
                    of this content was found on the pages below · {report.phrases_found} of {report.phrases_searched} searched
                    sentences appear online
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={Math.min(100, Number(percent) || 0)} color={colors.bar} sx={{ height: 6, borderRadius: 3 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                  {report.pages_fetched} page{report.pages_fetched === 1 ? "" : "s"} compared
                  {report.pages_failed > 0 && ` · ${report.pages_failed} could not be read`} · our own sites are ignored
                  {data.checked_at && <> · checked {format(new Date(data.checked_at), "d MMM yyyy HH:mm")}</>}
                </Typography>
              </Box>

              {isImport && report.sources?.length > 0 && (
                <Alert severity="info">
                  This is an uploaded past paper, so finding it online is expected: the matches below are likely its original source.
                </Alert>
              )}

              {report.sources?.length ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>{isImport ? "Found online (likely the original source)" : "Web page"}</TableCell>
                      <TableCell align="right">Overlap</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.sources.map((s) => <WebSourceRow key={s.url} source={s} thresholds={thresholds} />)}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">No matching web pages found.</Typography>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

/** Stand-alone web check dialog, for entities without an internal similarity report (question bank questions). */
export const WebSimilarityDialog = ({ open, base, title, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle sx={{ pb: 1 }}>
      Web plagiarism check
      {title && <Typography variant="body2" color="text.secondary">{title}</Typography>}
    </DialogTitle>
    <DialogContent dividers>{open && <WebSimilaritySection base={base} />}</DialogContent>
    <DialogActions>
      <Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button>
    </DialogActions>
  </Dialog>
);

export default WebSimilaritySection;
